import {
  SearchOutlined,
  SyncOutlined,
} from "@ant-design/icons";
import {
  Button,
  Form,
  notification,
  PageHeader,
  Row,
  Col,
  InputNumber,
  Input,
  Select,
  DatePicker,
} from "antd";
import { useState } from "react";

import { useAuth } from "../../context/auth";
import MerchantDropdown from "../Dropdown/MerchantDropdown";
import LandlordDropdown from "../Dropdown/LandlordDropdown";
import { listPaymentMethod } from "../../utils/formatter";
import IconReport from "../../static/icon/IconReport";

const BookingReport = () => {
  const { accessToken, adminRole } = useAuth();

  const [form] = Form.useForm();
  const selectedMerchants = Form.useWatch('merchantIds', form);

  const [loading, setLoading] = useState(false);

  async function download(searchValue) {
    setLoading(true);

    const url = new URL('/api/report/booking', window.location);
    if (searchValue.chargerId) url.searchParams.append('chargerId', searchValue.chargerId);
    if (searchValue.connectorId) url.searchParams.append('connectorId', searchValue.connectorId);
    if (searchValue.landlordIds) for (const landlordId of searchValue.landlordIds) url.searchParams.append('landlordId', landlordId);
    if (searchValue.merchantIds) for (const merchantId of searchValue.merchantIds) url.searchParams.append('merchantId', merchantId);
    if (searchValue.firstName) url.searchParams.append('firstName', searchValue.firstName);
    if (searchValue.lastName) url.searchParams.append('lastName', searchValue.lastName);
    if (searchValue.email) url.searchParams.append('email', searchValue.email);
    if (searchValue.tel) url.searchParams.append('tel', searchValue.tel);
    if (searchValue.paymentMethod) url.searchParams.append('paymentMethod', searchValue.paymentMethod);
    if (searchValue.createdAfter) url.searchParams.append('createdAfter', searchValue.createdAfter.startOf('day').toISOString());
    if (searchValue.createdBefore) url.searchParams.append('createdBefore', searchValue.createdBefore.endOf('day').toISOString());
    if (searchValue.paymentAfter) url.searchParams.append('paymentAfter', searchValue.paymentAfter.startOf('day').toISOString());
    if (searchValue.paymentBefore) url.searchParams.append('paymentBefore', searchValue.paymentBefore.endOf('day').toISOString());
    if (searchValue.style) url.searchParams.append('style', searchValue.style);

    const res = await fetch(url, {
      headers: { 'X-ACCESS-TOKEN': accessToken },
    });
    if (res.ok) {
      const a = document.createElement('a');
      a.href = URL.createObjectURL(await res.blob());
      a.download = `Booking-Report_${new Date().toISOString()}.xlsx`;
      a.click();
    } else {
      if (res.status === 404) {
        notification["error"]({
          message: "No matched booking found",
        });
      } else {
        notification["error"]({
          message: "Something went wrong !",
          description: `Please re-login or contact web admin. (Code: ${await res.json().then(data => data.message)})`,
        });
      }
    }

    setLoading(false);
  };

  return (
    <>
      <div className="flex justify-between items-center mt-2 space-x-4">
        <PageHeader
          style={{ padding: 0, width: "100%" }}
          title={
            <span className="flex items-center mt-2">
              <IconReport /> &nbsp;&nbsp;Booking Report
            </span>
          }
        />
      </div>
      <div className="content">
        <div className="">
          <Form
            form={form}
            layout="vertical"
            onFinish={download}
          >
            <Row align="middle" gutter={16}>
              <Col className="gutter-row" span={4}>
                <Form.Item
                  label="Charger Id"
                  name="chargerId"
                  colon={false}
                  initialValue={null}
                >
                  <InputNumber
                    placeholder="Charger Id"
                    allowClear
                    className="w-full"
                  />
                </Form.Item>
              </Col>
              <Col className="gutter-row" span={4}>
                <Form.Item
                  label="Connector Id"
                  name="connectorId"
                  colon={false}
                  initialValue={null}
                >
                  <InputNumber
                    placeholder="Connector Id"
                    allowClear
                    className="w-full"
                  />
                </Form.Item>
              </Col>
              <Col className="gutter-row" span={4}>
                <Form.Item
                  label="Name"
                  name="firstName"
                  colon={false}
                  initialValue={null}
                >
                  <Input
                    placeholder="Name"
                    allowClear
                    className="w-full"
                  />
                </Form.Item>
              </Col>
              <Col className="gutter-row" span={4}>
                <Form.Item
                  label="Surname"
                  name="lastName"
                  colon={false}
                  initialValue={null}
                >
                  <Input
                    placeholder="Surname"
                    allowClear
                    className="w-full"
                  />
                </Form.Item>
              </Col>
              <Col className="gutter-row" span={4}>
                <Form.Item
                  label="Email"
                  name="email"
                  colon={false}
                  initialValue={null}
                >
                  <Input
                    placeholder="Email"
                    allowClear
                    className="w-full"
                  />
                </Form.Item>
              </Col>
              <Col className="gutter-row" span={4}>
                <Form.Item
                  label="Mobile Number"
                  name="tel"
                  colon={false}
                  initialValue={null}
                >
                  <Input
                    placeholder="Mobile Number"
                    allowClear
                    className="w-full"
                  />
                </Form.Item>
              </Col>
              {['admin', 'callcenter', 'marketting'].includes(adminRole) && (
                <Col className="gutter-row" span={6}>
                  <Form.Item
                    label="Cluster Name"
                    name="merchantIds"
                    colon={false}
                    initialValue={[]}
                  >
                    <MerchantDropdown
                      allowClear
                      allowSearch
                      mode="multiple"
                      setSelectedMerchants={(merchantIds) => form.setFieldsValue({ merchantIds })}
                    />
                  </Form.Item>
                </Col>
              )}
              {['admin', 'merchant', 'party', 'callcenter', 'station', 'marketting'].includes(adminRole) && (
                <Col className="gutter-row" span={6}>
                  <Form.Item
                    label="Site Name"
                    name="landlordIds"
                    colon={false}
                    initialValue={[]}
                  >
                    <LandlordDropdown
                      allowClear
                      allowSearch
                      mode="multiple"
                      merchantIds={selectedMerchants}
                      setSelectedLandlords={(landlordIds) => form.setFieldsValue({ landlordIds })}
                    />
                  </Form.Item>
                </Col>
              )}
              <Col className="gutter-row" span={4}>
                <Form.Item
                  label="Payment Method"
                  name="paymentMethod"
                  colon={false}
                  initialValue={null}
                >
                  <Select
                    placeholder="Payment Method"
                    allowClear
                    options={[
                      { value: null, label: 'Any Payment Method' },
                      ...listPaymentMethod.map(type => ({
                        value: type.name,
                        label: type.value,
                      }))
                    ]}
                  />
                </Form.Item>
              </Col>
              <Col className="gutter-row" span={4}>
                <Form.Item
                  label="Service Date From"
                  name="createdAfter"
                  colon={false}
                  initialValue={null}
                >
                  <DatePicker
                    placeholder="Service Date From"
                    allowClear
                    className="w-full"
                  />
                </Form.Item>
              </Col>
              <Col className="gutter-row" span={4}>
                <Form.Item
                  label="Service Date To"
                  name="createdBefore"
                  colon={false}
                  initialValue={null}
                >
                  <DatePicker
                    placeholder="Service Date To"
                    allowClear
                    className="w-full"
                  />
                </Form.Item>
              </Col>
              <Col className="gutter-row" span={4}>
                <Form.Item
                  label="Payment Date From"
                  name="paymentAfter"
                  colon={false}
                  initialValue={null}
                >
                  <DatePicker
                    placeholder="Payment Date From"
                    allowClear
                    className="w-full"
                  />
                </Form.Item>
              </Col>
              <Col className="gutter-row" span={4}>
                <Form.Item
                  label="Payment Date To"
                  name="paymentBefore"
                  colon={false}
                  initialValue={null}
                >
                  <DatePicker
                    placeholder="Payment Date To"
                    allowClear
                    className="w-full"
                  />
                </Form.Item>
              </Col>
              <Col className="gutter-row" span={4}>
                <Form.Item
                  label="Style"
                  name="style"
                  colon={false}
                  initialValue="processed"
                >
                  <Select
                    placeholder="Style"
                    options={[
                      { value: 'raw', label: 'Raw', active: ['admin', 'merchant', 'callcenter', 'marketting'].includes(adminRole) },
                      { value: 'processed', label: 'Processed', active: true },
                    ].filter(item => item.active)}
                  />
                </Form.Item>
              </Col>
              <Col className="gutter-row" span={24}>
                <div className="flex">
                  <Button
                    className="w-28"
                    type="primary"
                    shape="round"
                    htmlType="submit"
                    icon={<SearchOutlined />}
                    loading={loading}
                  >
                    Search
                  </Button>

                  <Button
                    className="w-28 ml-2"
                    type="primary"
                    ghost
                    shape="round"
                    htmlType="reset"
                    icon={<SyncOutlined />}
                  >
                    Clear Data
                  </Button>
                </div>
              </Col>
            </Row>
          </Form>
        </div>
      </div>
    </>
  );
};

export default BookingReport;
